import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { TNotificationStyle, TNoteListStyle } from '@/types'
import { observe } from 'jotai-effect'
import { isSmallScreenAtom } from './screenSize'

const u = window.hallway.universe

// --- Stored preferences (persisted to localStorage via atomWithStorage) ---

export const notificationListStyleAtom = atomWithStorage<TNotificationStyle>(
  's:notificationListStyle',
  u.notificationListStyleDefault || 'detailed',
  undefined,
  { getOnInit: true }
)

export const noteListStyleAtom = atomWithStorage<TNoteListStyle>(
  's:noteListStyle',
  u.noteListStyleDefault || 'full',
  undefined,
  { getOnInit: true }
)

export const sidebarCollapseAtom = atomWithStorage<boolean>(
  's:sidebarCollapse',
  u.sidebarCollapseDefault ?? false,
  undefined,
  { getOnInit: true }
)

export const enableSingleColumnLayoutSettingAtom = atomWithStorage<boolean>(
  's:enableSingleColumnLayout',
  u.enableSingleColumnLayoutDefault ?? false,
  undefined,
  { getOnInit: true }
)

export const hideNotificationsAtom = atomWithStorage<boolean>(
  's:hideNotifications',
  u.hideNotificationsDefault ?? false,
  undefined,
  { getOnInit: true }
)

export const hideConversationsAtom = atomWithStorage<boolean>(
  's:hideConversations',
  u.hideConversationsDefault ?? false,
  undefined,
  { getOnInit: true }
)

export const hideFollowingAtom = atomWithStorage<boolean>(
  's:hideFollowing',
  u.hideFollowingDefault ?? false,
  undefined,
  { getOnInit: true }
)

export const hideFavoriteRelaysAtom = atomWithStorage<boolean>(
  's:hideFavoriteRelays',
  u.hideFavoriteRelaysDefault ?? false,
  undefined,
  { getOnInit: true }
)

export const defaultShowKindsAtom = atomWithStorage<number[]>(
  's:defaultShowKinds',
  getDefaultShowKinds(),
  undefined,
  { getOnInit: true }
)

function getDefaultShowKinds(): number[] {
  const { feedKinds, kindGroups } = u

  if (!kindGroups || kindGroups.length === 0) {
    return [...feedKinds]
  }

  const grouped = new Set<number>()
  const result: number[] = []

  for (const group of kindGroups) {
    if (!group.disabled) {
      for (const k of group.kinds) result.push(k)
    }
    for (const k of group.kinds) grouped.add(k)
  }

  for (const k of feedKinds) {
    if (!grouped.has(k)) result.push(k)
  }

  return result
}

// On small screens, single column layout is always forced on
export const enableSingleColumnLayoutAtom = atom<boolean>((get) => {
  const isSmall = get(isSmallScreenAtom)
  return isSmall ? true : get(enableSingleColumnLayoutSettingAtom)
})

// Auto-load media (computed from network connection, not stored)
export const autoLoadMediaAtom = atom<boolean>(false)
autoLoadMediaAtom.onMount = (setAtom) => {
  const connection = (navigator as any).connection
  if (!connection) {
    setAtom(true)
    return
  }

  const handleConnectionChange = () => {
    if (connection.type) setAtom(connection.type === 'wifi' || connection.type === 'ethernet')
    else setAtom(connection.effectiveType === '4g')
  }

  connection.addEventListener('change', handleConnectionChange)
  handleConnectionChange()

  return () => {
    connection.removeEventListener('change', handleConnectionChange)
  }
}

// CSS side-effect: overflow-y when in single-column layout
observe((get) => {
  const isSmall = get(isSmallScreenAtom)
  const singleColumn = get(enableSingleColumnLayoutAtom)

  if (!isSmall && singleColumn) {
    document.documentElement.style.setProperty('overflow-y', 'scroll')
  } else {
    document.documentElement.style.removeProperty('overflow-y')
  }
})
